import { Button, Checkbox, Flex, Label } from "@suns/design-system";
// import { Button, Flex, SelectOption, Select } from "@suns/design-system";
import { FilterValues } from "../all-reports-listings";
import { X } from "lucide-react";
import { Filter } from "@suns/design-system/src/components/Filter/Filter";
import { PlayerFilter, TeamFilter } from "@/components";
import { SearchType } from "@/components/ModelSearch/ModelSearch";
import { PlayerLevel } from "@suns/api";
import { useAccount } from "@/shared/hooks";
import { PersonnelFilter } from "@/components/PersonnelFilter/PersonnelFilter";
import { AgentFilter } from "@/components/AgentFilter/AgentFilter";
import { useState } from "react";

interface ReportFiltersProps {
  onChange: (filterValues: FilterValues) => void;
  onClear: () => void;
  filterValues: FilterValues;
  authors?: {
    key: string;
    name: string;
  }[];
  authorsLoading?: boolean;
  searchType?: SearchType[];
}

export default function ReportFilters({
  onChange,
  onClear,
  filterValues,
  authors,
  authorsLoading,
}: ReportFiltersProps) {
  const { hasAdminAccess } = useAccount();
  const [proChecked, setProChecked] = useState(
    filterValues.level == PlayerLevel.PRO
  );
  const [amateurChecked, setAmateurChecked] = useState(
    filterValues.level == PlayerLevel.AMATEUR
  );

  const proOnCheckedChange = () => {
    const newProChecked = !proChecked;

    if (newProChecked && amateurChecked) {
      setProChecked(false);
      setAmateurChecked(false);
      onChange({ ...filterValues, level: undefined });
      return;
    }

    setProChecked(newProChecked);
    setAmateurChecked(false);

    onChange({
      ...filterValues,
      level: newProChecked ? PlayerLevel.PRO : undefined,
    });
  };

  const amateurOnCheckedChange = () => {
    const newAmateurChecked = !amateurChecked;

    if (newAmateurChecked && proChecked) {
      setProChecked(false);
      setAmateurChecked(false);
      onChange({ ...filterValues, level: undefined });
      return;
    }

    setAmateurChecked(newAmateurChecked);
    setProChecked(false);

    onChange({
      ...filterValues,
      level: newAmateurChecked ? PlayerLevel.AMATEUR : undefined,
    });
  };

  return (
    <Flex gap="sm" align="center" wrap={true} className="w-full">
      <Flex direction="right" gap="sm" align="center">
        <Label className="cursor-pointer">
          <Checkbox checked={proChecked} onCheckedChange={proOnCheckedChange} />
          <div className="grid gap-1.5 font-normal">
            <p className="text-sm font-medium leading-none">Pro</p>
          </div>
        </Label>
      </Flex>

      <Flex direction="right" gap="sm" align="center">
        <Label className="cursor-pointer">
          <Checkbox
            checked={amateurChecked}
            onCheckedChange={amateurOnCheckedChange}
          />
          <div className="grid gap-1.5 font-normal">
            <p className="text-sm font-medium leading-none">Amateur</p>
          </div>
        </Label>
      </Flex>

      <PlayerFilter
        value={filterValues.playerIds?.map(String) || []}
        onChange={(selectedKeys: string[]) => {
          onChange({ ...filterValues, playerIds: selectedKeys.map(Number) });
        }}
      />

      <TeamFilter
        value={filterValues.teamIds?.map(String) || []}
        onChange={(selectedKeys: string[]) => {
          onChange({ ...filterValues, teamIds: selectedKeys.map(Number) });
        }}
      />

      {hasAdminAccess && filterValues.tab == "intel" ? (
        <AgentFilter
          value={
            filterValues.agentIds?.map((id: number) => id.toString()) || []
          }
          onChange={(selectedKeys: string[]) => {
            onChange({ ...filterValues, agentIds: selectedKeys.map(Number) });
          }}
        />
      ) : null}

      {hasAdminAccess && filterValues.tab == "intel" ? (
        <PersonnelFilter
          value={
            filterValues.personnelIds?.map((id: number) => id.toString()) || []
          }
          onChange={(selectedKeys: string[]) => {
            onChange({
              ...filterValues,
              personnelIds: selectedKeys.map(Number),
            });
          }}
        />
      ) : null}

      {authors && (
        <Filter
          avoidCollisions={false}
          title={authorsLoading ? "Loading..." : "Author"}
          value={filterValues.authorUsername || []}
          onChange={(value) =>
            onChange({
              ...filterValues,
              authorUsername: value as string[],
            })
          }
          options={authors?.map((author) => ({
            key: author.key,
            name: author.name,
          }))}
        />
      )}
      <Button variant="ghost" onClick={onClear}>
        <Flex gap="xs" align="center" justify="center">
          Reset
          <X className="w-4 pt-0.5" />
        </Flex>
      </Button>
    </Flex>
  );
}
